import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LoginRequest } from "../../types";
import { useTranslation } from "../../contexts/TranslationContext";
import ErrorAlert from "../common/ErrorAlert";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<LoginRequest>({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent double submission
    if (isSubmitting || loading) {
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    setError("");

    try {
      await login(formData);
      navigate("/forms");
    } catch (err: any) {
      // Handle specific HTTP error codes
      if (err.response?.status === 429) {
        setError(t("auth.login.tooManyAttempts"));
      } else if (err.response?.status === 401) {
        setError(t("auth.login.invalidCredentials"));
      } else {
        const errorMessage =
          err.response?.data?.detail ||
          err.message ||
          t("auth.login.loginFailed");
        setError(errorMessage);
      }
      // Explicitly prevent any navigation on error
    } finally {
      setLoading(false);
      setIsSubmitting(false);
      console.log("Login attempt completed");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Clear error when user starts typing
    if (error) {
      setError("");
    }
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t("auth.login.title")}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t("auth.login.subtitle")}
          </p>
        </div>
        <form
          ref={formRef}
          className="mt-8 space-y-6"
          onSubmit={handleSubmit}
          noValidate
        >
          {error && <ErrorAlert message={error} />}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="username" className="sr-only">
                {t("auth.login.usernameLabel")}
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder={t("auth.login.username")}
                value={formData.username}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                {t("auth.login.passwordLabel")}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder={t("auth.login.password")}
                value={formData.password}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t("auth.login.signingIn") : t("auth.login.signIn")}
            </button>
          </div>

          <div className="text-center">
            <span className="text-sm text-gray-600">
              Need an account? Contact your administrator.
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
